import { useContext, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { DELETE_SHIPPING_PARTNER } from '@/components/Account/ShippersTab/mutations';
import {
    GET_ORG_CONTRACTOR_PARTNERSHIPS,
    INSERT_CONTRACTOR_PARTNERSHIPS_WITH_PRICING,
} from '@/components/CorporateUsers/graphql';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { linkWritesFor } from './utils';

// The store reads links from GetOrgContractorPartnerships and has no refetch of its own for it.
// App.js caches the Flex Center's ClientById, whose partner_contractors feed Dispatch Plan's carriers.
const CONTRACTOR_PARTNERSHIP_AND_PRICING_QUERIES = [
    'GetOrgContractorPartnerships',
    'ClientById',
    'GetCorpPricingOverrides',
];

/**
 * @typedef {object} LinkTariff
 * @property {string} tariff_id
 * @property {string} algo_type
 * @property {string} [name]
 */

/** The Flex Center's link picker selection and pay tariff, and link / unlink against them. */
export const useContractorLinks = () => {
    const apolloClient = useApolloClient();
    const { state, callbacks } = useContext(CorpAdminContext);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedTariff, setSelectedTariff] = useState(/** @type {LinkTariff|null} */ (null));
    const mutationOptions = { refetchQueries: CONTRACTOR_PARTNERSHIP_AND_PRICING_QUERIES, awaitRefetchQueries: true };
    const [insertLinks, { loading: linking }] = useMutation(
        INSERT_CONTRACTOR_PARTNERSHIPS_WITH_PRICING,
        mutationOptions
    );
    const [deleteLink, { loading: unlinking }] = useMutation(DELETE_SHIPPING_PARTNER, mutationOptions);

    const linkSelected = async (flexCenterRow) => {
        let writes;
        let linked;
        try {
            // Read fresh so a review saved since the page loaded sets the status, not the cached links.
            const orgLinksResult = await apolloClient.query({
                query: GET_ORG_CONTRACTOR_PARTNERSHIPS,
                variables: { orgFlexCenterIds: state.orgFlexCenterIds },
                fetchPolicy: 'network-only',
            });
            writes = linkWritesFor({
                rows: selectedRows,
                flexCenterId: flexCenterRow.client_id,
                tariff: selectedTariff,
                pricingByContractor: state.flexCenterPricingByContractor,
                orgLinks: orgLinksResult.data.partnerships,
            });

            linked = await insertLinks({ variables: writes });
        } catch (linkError) {
            // Another tab can link the same carrier, or give it pay here, between this list loading and the save.
            const alreadyLinked = linkError.message.includes('partnerships_pairing_unique_idx');
            const alreadyPriced = linkError.message.includes('marketplace_idx_po');
            if (!alreadyLinked && !alreadyPriced) {
                Sentry.captureException(linkError);
                callbacks.setNotification({ severity: 'error', message: "Couldn't link those carriers." });
                return;
            }

            await apolloClient.refetchQueries({ include: CONTRACTOR_PARTNERSHIP_AND_PRICING_QUERIES });
            let conflictMessage = 'Nothing was linked: one of those carriers is already linked to this Flex Center.';
            if (alreadyPriced)
                conflictMessage = 'Nothing was linked: one of those carriers already has pricing here. Try again.';
            callbacks.setNotification({ severity: 'error', message: conflictMessage });
            return;
        }

        setSelectedRows([]);
        setSelectedTariff(null);

        // An update whose ids were deleted since the list loaded matches nothing and still succeeds.
        const repricedCount = linked.data.update_pricing_overrides.affected_rows;
        if (repricedCount < writes.repricedIds.length) {
            callbacks.setNotification({
                severity: 'warning',
                message: `Linked to ${flexCenterRow.business_name}, but a carrier's pay row was removed meanwhile, so it has no pay here. Set it on the Carriers tab.`,
            });
            return;
        }

        let message = `Linked to ${flexCenterRow.business_name}.`;
        if (selectedTariff?.name) message = `Linked to ${flexCenterRow.business_name} on ${selectedTariff.name}.`;
        callbacks.setNotification({ severity: 'success', message });
    };

    const unlink = async (contractorRow, partnershipId) => {
        try {
            await deleteLink({ variables: { partnership_id: partnershipId } });
        } catch (deleteError) {
            Sentry.captureException(deleteError);
            callbacks.setNotification({
                severity: 'error',
                message: `Couldn't unlink ${contractorRow.business_name}.`,
            });
            return;
        }

        callbacks.setNotification({ severity: 'success', message: `Unlinked ${contractorRow.business_name}.` });
    };

    return {
        selectedRows,
        setSelectedRows,
        selectedTariff,
        setSelectedTariff,
        linking,
        unlinking,
        linkSelected,
        unlink,
    };
};
